import { Controller } from "@hotwired/stimulus"
import { HistogramSeries, LineSeries } from "lightweight-charts"
import { createCandleChart, UP, DOWN } from "../lib/candle_chart"
import { subscribeToCandles } from "../channels/candles_subscription"

const PAGE_SIZE = 500
// Prefetch the next page once the viewport's left edge is within this many
// bars of the oldest loaded candle — comfortably before the user hits the
// edge, so scroll-back stays seamless. Must stay below PAGE_SIZE.
const LOAD_MORE_THRESHOLD = 200

// Indicator series rendered on the chart. Each entry maps a stable key to the
// pane it lives in and how its points are extracted from the server payload.
const INDICATOR_KEYS = [
  "ema20", "ema50", "bbUpper", "bbMiddle", "bbLower",
  "rsi", "macdLine", "macdSignal", "macdHist"
]

export default class extends Controller {
  static targets = ["container", "timeframeButton"]
  static values = {
    pairId: Number,
    timeframe: { type: String, default: "1h" }
  }

  activate() {
    // Re-shown tab: the chart, its series, and loaded candles are all still
    // here. Leave the view exactly as the user left it — `autoSize` re-renders
    // it at the restored container size. Calling fitContent() here would
    // discard the user's scroll/zoom position on every tab switch.
    if (this.chart) return

    this.buildChart()
    this.reload()
    this.subscribe()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
  }

  buildChart() {
    const { chart, candleSeries, volumeSeries } = createCandleChart(this.containerTarget)
    this.chart = chart
    this.candleSeries = candleSeries
    this.volumeSeries = volumeSeries

    this.buildIndicatorSeries()

    this.chart
      .timeScale()
      .subscribeVisibleLogicalRangeChange((range) => {
        if (!range) return
        if (range.from < LOAD_MORE_THRESHOLD && this.hasMore && !this.loading) {
          this.loadOlder()
        }
      })
  }

  // EMA + Bollinger overlay the price (pane 0); RSI and MACD get their own
  // stacked panes below.
  buildIndicatorSeries() {
    const overlay = { priceLineVisible: false, lastValueVisible: false, lineWidth: 1 }

    this.indicatorSeries = {
      ema20: this.chart.addSeries(LineSeries, { ...overlay, color: "#facc15" }),
      ema50: this.chart.addSeries(LineSeries, { ...overlay, color: "#38bdf8" }),
      bbUpper: this.chart.addSeries(LineSeries, { ...overlay, color: "#6b7280" }),
      bbMiddle: this.chart.addSeries(LineSeries, { ...overlay, color: "#4b5563" }),
      bbLower: this.chart.addSeries(LineSeries, { ...overlay, color: "#6b7280" }),
      rsi: this.chart.addSeries(LineSeries, { color: "#c084fc", lineWidth: 1 }, 1),
      macdHist: this.chart.addSeries(HistogramSeries, { priceLineVisible: false }, 2),
      macdLine: this.chart.addSeries(LineSeries, { color: "#38bdf8", lineWidth: 1 }, 2),
      macdSignal: this.chart.addSeries(LineSeries, { color: "#f59e0b", lineWidth: 1 }, 2)
    }

    // RSI 30/70 guide lines (lineStyle 2 = dashed).
    ;[30, 70].forEach((price) => {
      this.indicatorSeries.rsi.createPriceLine({
        price, color: "#2b2e34", lineWidth: 1, lineStyle: 2, axisLabelVisible: true
      })
    })

    // Give the price pane the most room.
    const panes = this.chart.panes()
    if (panes[0]) panes[0].setStretchFactor(3)
    if (panes[1]) panes[1].setStretchFactor(1)
    if (panes[2]) panes[2].setStretchFactor(1)
  }

  subscribe() {
    this.subscription = subscribeToCandles({
      pairId: this.pairIdValue,
      timeframe: this.timeframeValue,
      onUpdate: (data) => this.applyLiveCandles(data.candles, data.indicators)
    })
  }

  resubscribe() {
    if (this.subscription) this.subscription.unsubscribe()
    this.subscribe()
  }

  // Splits the server `indicators` block into one ascending point array per
  // indicator series key.
  normalizeIndicators(indicators) {
    const empty = Object.fromEntries(INDICATOR_KEYS.map((k) => [k, []]))
    if (!indicators) return empty

    const pick = (rows, key) => (rows || []).map((p) => ({ time: p.time, value: p[key] }))

    return {
      ema20: (indicators.ema && indicators.ema["20"]) || [],
      ema50: (indicators.ema && indicators.ema["50"]) || [],
      bbUpper: pick(indicators.bollinger, "upper"),
      bbMiddle: pick(indicators.bollinger, "middle"),
      bbLower: pick(indicators.bollinger, "lower"),
      rsi: indicators.rsi || [],
      macdLine: pick(indicators.macd, "macd"),
      macdSignal: pick(indicators.macd, "signal"),
      macdHist: (indicators.macd || []).map((p) => ({
        time: p.time,
        value: p.histogram,
        color: p.histogram >= 0 ? UP : DOWN
      }))
    }
  }

  // Applies pushed candles to the live series, keeping `this.bars` in sync so a
  // later lazy-load setData() does not clobber them.
  applyLiveCandles(candles, indicators) {
    candles.forEach((c) => {
      const last = this.bars[this.bars.length - 1]
      // lightweight-charts update() only accepts the last bar or a newer one.
      if (last && c.time < last.time) return

      if (last && c.time === last.time) {
        this.bars[this.bars.length - 1] = c
      } else {
        this.bars.push(c)
      }

      this.candleSeries.update({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close
      })
      this.volumeSeries.update({
        time: c.time,
        value: c.volume,
        color: c.close >= c.open ? UP : DOWN
      })
    })

    const live = this.normalizeIndicators(indicators)
    INDICATOR_KEYS.forEach((key) => {
      const store = this.indicators[key]
      live[key].forEach((point) => {
        const last = store[store.length - 1]
        if (last && point.time < last.time) return

        if (last && point.time === last.time) {
          store[store.length - 1] = point
        } else {
          store.push(point)
        }
        this.indicatorSeries[key].update(point)
      })
    })
  }

  switchTimeframe(event) {
    const timeframe = event.currentTarget.dataset.timeframe
    if (timeframe === this.timeframeValue) return
    this.timeframeValue = timeframe
    this.timeframeButtonTargets.forEach((btn) => {
      btn.classList.toggle("tf-active", btn.dataset.timeframe === timeframe)
    })
    this.reload()
    this.resubscribe()
  }

  reload() {
    this.bars = []
    this.cursor = null
    this.hasMore = true
    this.indicators = Object.fromEntries(INDICATOR_KEYS.map((k) => [k, []]))
    this.candleSeries.setData([])
    this.volumeSeries.setData([])
    INDICATOR_KEYS.forEach((key) => this.indicatorSeries[key].setData([]))
    this.loadOlder({ fit: true })
  }

  async loadOlder({ fit = false } = {}) {
    if (!this.hasMore || this.loading) return
    this.loading = true

    try {
      const data = await this.fetchCandles()
      const prepended = data.candles.length
      const timeScale = this.chart.timeScale()
      const rangeBefore = timeScale.getVisibleLogicalRange()

      // endpoint already returns ascending — prepend the older batch
      this.bars = data.candles.concat(this.bars)
      this.cursor = data.oldest_open_time
      this.hasMore = data.has_more

      this.candleSeries.setData(
        this.bars.map((c) => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close
        }))
      )
      this.volumeSeries.setData(
        this.bars.map((c) => ({
          time: c.time,
          value: c.volume,
          color: c.close >= c.open ? UP : DOWN
        }))
      )

      const older = this.normalizeIndicators(data.indicators)
      INDICATOR_KEYS.forEach((key) => {
        this.indicators[key] = older[key].concat(this.indicators[key])
        this.indicatorSeries[key].setData(this.indicators[key])
      })

      if (fit) {
        timeScale.fitContent()
      } else if (rangeBefore && prepended > 0) {
        // setData leaves the visible *logical* range unchanged, which would
        // dump the viewport onto the freshly-prepended oldest bars. Shift the
        // range forward by the prepended count so the user's candles stay put.
        timeScale.setVisibleLogicalRange({
          from: rangeBefore.from + prepended,
          to: rangeBefore.to + prepended
        })
      }
    } catch (error) {
      // hasMore stays true → a later scroll-back retries the fetch
      console.warn("price-chart: failed to load candles", error)
    } finally {
      this.loading = false
    }
  }

  async fetchCandles() {
    const url = new URL(
      `/trading_pairs/${this.pairIdValue}/candles.json`,
      window.location.origin
    )
    url.searchParams.set("timeframe", this.timeframeValue)
    url.searchParams.set("limit", String(PAGE_SIZE))
    if (this.cursor) url.searchParams.set("before", this.cursor)

    const response = await fetch(url)
    if (!response.ok) throw new Error(`candles request failed: ${response.status}`)
    return response.json()
  }
}
